import type { WalletSigner, EIP712SignParams } from '@ownyou/identity';
import {
  AUDIT_EVENT_SCHEMA_VERSION,
  type AuditAction,
  type AuditActor,
  type AuditEventV1,
  type AuditResult,
  type AuditTarget,
  assertAuditEventV1,
  canonicalizeAuditEventForHash,
  canonicalizeAuditEventForSigning,
} from '@ownyou/shared-types';

export const AUDIT_KEY_ALGORITHM = 'ECDSA-P256-SHA256' as const;
export const AUDIT_DELEGATION_SCHEMA_VERSION = 'audit-key-delegation.v1' as const;
export const AUDIT_DELEGATION_PURPOSE = 'ownyou.audit.events' as const;

export interface AuditKeyDelegationV1 {
  schemaVersion: typeof AUDIT_DELEGATION_SCHEMA_VERSION;
  delegationId: string;
  auditKeyId: string;
  walletAddress: string;
  deviceId: string;
  algorithm: typeof AUDIT_KEY_ALGORITHM;
  publicKeyJwk: JsonWebKey;
  publicKeyThumbprint: string;
  createdAt: number;
  purpose: typeof AUDIT_DELEGATION_PURPOSE;
  walletSignature: `0x${string}`;
}

export interface StoredAuditKey {
  auditKeyId: string;
  walletAddress: string;
  deviceId: string;
  algorithm: typeof AUDIT_KEY_ALGORITHM;
  createdAt: number;
  privateKey: CryptoKey;
  publicKeyJwk: JsonWebKey;
  delegation: AuditKeyDelegationV1;
}

export interface AuditKeyStore {
  loadActive(walletAddress: string, deviceId: string): Promise<StoredAuditKey | null>;
  loadByDelegation(walletAddress: string, deviceId: string, delegationId: string): Promise<StoredAuditKey | null>;
  saveActive(key: StoredAuditKey): Promise<void>;
}

export interface AuditEventDraftV1 {
  id?: string;
  userId?: string;
  ts?: number;
  actor: AuditActor;
  action: AuditAction;
  target: AuditTarget;
  result?: AuditResult;
  reason?: string;
  traceId?: string;
  routeId?: string;
  previousEventHash?: string;
}

export interface AuditEventVerificationResult {
  ok: boolean;
  errors: string[];
}

const AUDIT_DELEGATION_DOMAIN: EIP712SignParams['domain'] = {
  name: 'OwnYou AuditLog',
  version: '1',
};

const AUDIT_DELEGATION_TYPES: EIP712SignParams['types'] = {
  AuditKeyDelegation: [
    { name: 'auditKeyId', type: 'string' },
    { name: 'publicKeyThumbprint', type: 'string' },
    { name: 'algorithm', type: 'string' },
    { name: 'walletAddress', type: 'string' },
    { name: 'deviceId', type: 'string' },
    { name: 'createdAt', type: 'uint256' },
    { name: 'purpose', type: 'string' },
  ],
};

export class InMemoryAuditKeyStore implements AuditKeyStore {
  private keys = new Map<string, StoredAuditKey>();

  async loadActive(walletAddress: string, deviceId: string): Promise<StoredAuditKey | null> {
    return this.keys.get(activeStorageKey(walletAddress, deviceId)) ?? null;
  }

  async loadByDelegation(
    walletAddress: string,
    deviceId: string,
    delegationId: string,
  ): Promise<StoredAuditKey | null> {
    return this.keys.get(delegationStorageKey(walletAddress, deviceId, delegationId)) ?? null;
  }

  async saveActive(key: StoredAuditKey): Promise<void> {
    this.keys.set(activeStorageKey(key.walletAddress, key.deviceId), key);
    this.keys.set(delegationStorageKey(
      key.walletAddress,
      key.deviceId,
      key.delegation.delegationId,
    ), key);
  }
}

export class IndexedDbAuditKeyStore implements AuditKeyStore {
  private dbPromise: Promise<IDBDatabase> | null = null;

  constructor(
    private readonly dbName = 'ownyou_audit_keys',
    private readonly storeName = 'auditKeys',
  ) {}

  async loadActive(walletAddress: string, deviceId: string): Promise<StoredAuditKey | null> {
    const db = await this.openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(this.storeName, 'readonly');
      const request = tx.objectStore(this.storeName).get(activeStorageKey(walletAddress, deviceId));
      request.onsuccess = () => {
        const record = request.result as (StoredAuditKey & { storageKey: string }) | undefined;
        resolve(record ? stripStorageKey(record) : null);
      };
      request.onerror = () => reject(request.error);
    });
  }

  async loadByDelegation(
    walletAddress: string,
    deviceId: string,
    delegationId: string,
  ): Promise<StoredAuditKey | null> {
    const db = await this.openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(this.storeName, 'readonly');
      const request = tx.objectStore(this.storeName).get(delegationStorageKey(walletAddress, deviceId, delegationId));
      request.onsuccess = () => {
        const record = request.result as (StoredAuditKey & { storageKey: string }) | undefined;
        resolve(record ? stripStorageKey(record) : null);
      };
      request.onerror = () => reject(request.error);
    });
  }

  async saveActive(key: StoredAuditKey): Promise<void> {
    const db = await this.openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(this.storeName, 'readwrite');
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      const objectStore = tx.objectStore(this.storeName);
      objectStore.put({
        ...key,
        storageKey: activeStorageKey(key.walletAddress, key.deviceId),
      });
      objectStore.put({
        ...key,
        storageKey: delegationStorageKey(
          key.walletAddress,
          key.deviceId,
          key.delegation.delegationId,
        ),
      });
    });
  }

  private openDb(): Promise<IDBDatabase> {
    this.dbPromise ??= new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, 1);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(this.storeName)) {
          db.createObjectStore(this.storeName, { keyPath: 'storageKey' });
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
    return this.dbPromise;
  }
}

export async function getOrCreateDelegatedAuditKey(params: {
  signer: WalletSigner;
  deviceId: string;
  keyStore: AuditKeyStore;
  now?: () => number;
}): Promise<StoredAuditKey> {
  const walletAddress = params.signer.getAddress();
  const existing = await params.keyStore.loadActive(walletAddress, params.deviceId);
  if (existing) return existing;

  const createdAt = params.now?.() ?? Date.now();
  const keyPair = await crypto.subtle.generateKey(
    { name: 'ECDSA', namedCurve: 'P-256' },
    false,
    ['sign', 'verify'],
  );
  const publicKeyJwk = await crypto.subtle.exportKey('jwk', keyPair.publicKey);
  const publicKeyThumbprint = await thumbprintPublicJwk(publicKeyJwk);
  const auditKeyId = `audit-key:${publicKeyThumbprint.slice(0, 24)}`;
  const delegationId = `audit-delegation:${publicKeyThumbprint.slice(0, 24)}:${createdAt}`;
  const walletSignature = await params.signer.signTypedData({
    domain: AUDIT_DELEGATION_DOMAIN,
    types: AUDIT_DELEGATION_TYPES,
    primaryType: 'AuditKeyDelegation',
    message: {
      auditKeyId,
      publicKeyThumbprint,
      algorithm: AUDIT_KEY_ALGORITHM,
      walletAddress,
      deviceId: params.deviceId,
      createdAt,
      purpose: AUDIT_DELEGATION_PURPOSE,
    },
  });

  const delegation: AuditKeyDelegationV1 = {
    schemaVersion: AUDIT_DELEGATION_SCHEMA_VERSION,
    delegationId,
    auditKeyId,
    walletAddress,
    deviceId: params.deviceId,
    algorithm: AUDIT_KEY_ALGORITHM,
    publicKeyJwk,
    publicKeyThumbprint,
    createdAt,
    purpose: AUDIT_DELEGATION_PURPOSE,
    walletSignature,
  };

  const stored: StoredAuditKey = {
    auditKeyId,
    walletAddress,
    deviceId: params.deviceId,
    algorithm: AUDIT_KEY_ALGORITHM,
    createdAt,
    privateKey: keyPair.privateKey,
    publicKeyJwk,
    delegation,
  };
  await params.keyStore.saveActive(stored);
  return stored;
}

export async function signAuditEvent(
  key: StoredAuditKey,
  draft: AuditEventDraftV1,
): Promise<AuditEventV1> {
  const eventBase: AuditEventV1 = {
    schemaVersion: AUDIT_EVENT_SCHEMA_VERSION,
    id: draft.id ?? randomId('audit-event'),
    userId: draft.userId ?? key.walletAddress,
    deviceId: key.deviceId,
    auditKeyId: key.auditKeyId,
    delegationId: key.delegation.delegationId,
    algorithm: key.algorithm,
    ts: draft.ts ?? Date.now(),
    actor: draft.actor,
    action: draft.action,
    target: draft.target,
    ...(draft.result !== undefined ? { result: draft.result } : {}),
    ...(draft.reason !== undefined ? { reason: draft.reason } : {}),
    ...(draft.traceId !== undefined ? { traceId: draft.traceId } : {}),
    ...(draft.routeId !== undefined ? { routeId: draft.routeId } : {}),
    ...(draft.previousEventHash !== undefined ? { previousEventHash: draft.previousEventHash } : {}),
    eventHash: '',
    signature: '',
  };

  const eventHash = await sha256Hex(canonicalizeAuditEventForHash(eventBase));
  const eventWithHash = { ...eventBase, eventHash };
  assertAuditEventV1({ ...eventWithHash, signature: 'pending' });
  const signatureBytes = await crypto.subtle.sign(
    { name: 'ECDSA', hash: 'SHA-256' },
    key.privateKey,
    new TextEncoder().encode(canonicalizeAuditEventForSigning({ ...eventWithHash, signature: '' })),
  );
  const event = {
    ...eventWithHash,
    signature: bytesToBase64Url(new Uint8Array(signatureBytes)),
  };
  assertAuditEventV1(event);
  return event;
}

export async function verifyAuditEvent(
  event: AuditEventV1,
  delegation: AuditKeyDelegationV1,
): Promise<AuditEventVerificationResult> {
  const errors: string[] = [];
  const validation = (() => {
    try {
      assertAuditEventV1(event);
      return null;
    } catch (error) {
      return error instanceof Error ? error.message : String(error);
    }
  })();
  if (validation) errors.push(validation);

  if (!isDelegationStructurallyValid(delegation)) {
    errors.push('Invalid Audit key delegation metadata');
  }
  if (event.auditKeyId !== delegation.auditKeyId) {
    errors.push('Audit event key does not match delegation');
  }
  if (event.delegationId !== delegation.delegationId) {
    errors.push('Audit event delegation does not match delegation metadata');
  }
  if (event.algorithm !== delegation.algorithm) {
    errors.push('Audit event algorithm does not match delegation metadata');
  }
  if (event.deviceId !== delegation.deviceId) {
    errors.push('Audit event device does not match delegation metadata');
  }
  if (event.userId.toLowerCase() !== delegation.walletAddress.toLowerCase()) {
    errors.push('Audit event user does not match delegated wallet');
  }

  const expectedThumbprint = await thumbprintPublicJwk(delegation.publicKeyJwk);
  if (expectedThumbprint !== delegation.publicKeyThumbprint) {
    errors.push('Delegation public key thumbprint mismatch');
  }

  const expectedHash = await sha256Hex(canonicalizeAuditEventForHash(event));
  if (expectedHash !== event.eventHash) {
    errors.push('Audit event hash mismatch');
  }

  if (errors.length > 0) {
    return { ok: false, errors };
  }

  try {
    const publicKey = await crypto.subtle.importKey(
      'jwk',
      delegation.publicKeyJwk,
      { name: 'ECDSA', namedCurve: 'P-256' },
      false,
      ['verify'],
    );
    const signatureOk = await crypto.subtle.verify(
      { name: 'ECDSA', hash: 'SHA-256' },
      publicKey,
      base64UrlToBytes(event.signature).buffer as ArrayBuffer,
      new TextEncoder().encode(canonicalizeAuditEventForSigning(event)),
    );
    if (!signatureOk) {
      return { ok: false, errors: ['Audit event signature invalid'] };
    }
  } catch (error) {
    return {
      ok: false,
      errors: [error instanceof Error ? error.message : String(error)],
    };
  }

  return { ok: true, errors: [] };
}

function isDelegationStructurallyValid(delegation: AuditKeyDelegationV1): boolean {
  return (
    delegation.schemaVersion === AUDIT_DELEGATION_SCHEMA_VERSION &&
    delegation.algorithm === AUDIT_KEY_ALGORITHM &&
    delegation.purpose === AUDIT_DELEGATION_PURPOSE &&
    delegation.walletSignature.startsWith('0x') &&
    Boolean(delegation.auditKeyId) &&
    Boolean(delegation.delegationId) &&
    Boolean(delegation.walletAddress) &&
    Boolean(delegation.deviceId) &&
    typeof delegation.createdAt === 'number' &&
    Number.isFinite(delegation.createdAt)
  );
}

async function thumbprintPublicJwk(jwk: JsonWebKey): Promise<string> {
  const canonical = JSON.stringify({
    crv: jwk.crv,
    kty: jwk.kty,
    x: jwk.x,
    y: jwk.y,
  });
  return sha256Hex(canonical);
}

async function sha256Hex(value: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
  return bytesToHex(new Uint8Array(digest));
}

function randomId(prefix: string): string {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  return `${prefix}:${bytesToHex(bytes)}`;
}

function activeStorageKey(walletAddress: string, deviceId: string): string {
  return `active:${walletAddress.toLowerCase()}:${deviceId}`;
}

function delegationStorageKey(walletAddress: string, deviceId: string, delegationId: string): string {
  return `delegation:${walletAddress.toLowerCase()}:${deviceId}:${delegationId}`;
}

function stripStorageKey(record: StoredAuditKey & { storageKey: string }): StoredAuditKey {
  const { storageKey: _storageKey, ...key } = record;
  return key;
}

function bytesToHex(bytes: Uint8Array): string {
  return [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

function bytesToBase64Url(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function base64UrlToBytes(value: string): Uint8Array {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/').padEnd(
    Math.ceil(value.length / 4) * 4,
    '=',
  );
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}
